/**
 * Administrator settings for this facility: which care programmes (content packs) are on, and
 * whether rules that are not yet clinically approved are shown in a supervised pilot.
 */
import {
  type EpisodeOfCare,
  type Patient,
  isRemovedEpisode,
  isRemovedPatient,
  removalOf,
} from '@assist/core';
import { useEffect, useState } from 'preact/hooks';
import { RemovedBanner } from '../components/Removal.js';
import { app } from '../controller.js';
import { useApp } from '../hooks.js';
import { t } from '../i18n.js';
import { allPacks } from '../packs.js';
import { displayName, mrnOf } from '../patient.js';

export function Settings() {
  const a = useApp();
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Show the new state at once; it is replaced by the server's answer (or reverted on failure).
  const [shown, setShown] = useState<Record<string, { enabled: boolean; allowDraft: boolean }>>({});
  if (a.session?.user.role !== 'admin') return <p class="muted">Admins only.</p>;
  const change = async (packId: string, enabled: boolean, allowDraft: boolean) => {
    setBusy(packId);
    setErr(null);
    setShown((x) => ({ ...x, [packId]: { enabled, allowDraft } }));
    try {
      await app.setPack(packId, enabled, allowDraft);
    } catch (e) {
      setErr(a.online ? (e instanceof Error ? e.message : String(e)) : t.needsOnline);
    } finally {
      setShown((x) => {
        const { [packId]: _done, ...rest } = x;
        return rest;
      });
      setBusy(null);
    }
  };
  return (
    <section>
      <div class="card">
        <h3>{t.packsTitle}</h3>
        <p class="muted">{t.packsHelp}</p>
        {err ? <div class="alert">{err}</div> : null}
      </div>
      <FacilityIds />
      {allPacks.map((p) => {
        const s = shown[p.id] ?? a.packSettings.find((x) => x.packId === p.id);
        const enabled = s?.enabled ?? false;
        const allowDraft = s?.allowDraft ?? false;
        const approved = p.rules.filter((r) => r.status === 'approved').length;
        const total = p.rules.filter((r) => r.status !== 'retired').length;
        return (
          <div class="card" key={p.id} data-pack={p.id}>
            <h3>
              {p.title} <span class="muted">v{p.version}</span>
            </h3>
            <p>{p.description}</p>
            <p class="muted">{t.rulesCount(approved, total)}</p>
            <label class="check">
              <input
                type="checkbox"
                checked={enabled}
                disabled={busy === p.id}
                onChange={(e) =>
                  void change(p.id, (e.target as HTMLInputElement).checked, allowDraft)
                }
              />{' '}
              {t.enabled}
            </label>
            <label class="check">
              <input
                type="checkbox"
                checked={allowDraft}
                disabled={busy === p.id || !enabled}
                onChange={(e) => void change(p.id, enabled, (e.target as HTMLInputElement).checked)}
              />{' '}
              {t.pilotDrafts}
            </label>
          </div>
        );
      })}
      <RemovedRecords />
    </section>
  );
}

interface FacilityInfo {
  dhis2OrgUnit: string | null;
  mfrId: string | null;
  dhis2: { configured: boolean; dryRun?: boolean };
}

/** DHIS2 org unit and Master Facility Registry id of this facility (online, admin). */
function FacilityIds() {
  const a = useApp();
  const [info, setInfo] = useState<FacilityInfo | null>(null);
  const [ou, setOu] = useState('');
  const [mfr, setMfr] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: load once when online
  useEffect(() => {
    void app
      .api<FacilityInfo>('/api/admin/facility')
      .then((f) => {
        setInfo(f);
        setOu(f.dhis2OrgUnit ?? '');
        setMfr(f.mfrId ?? '');
      })
      .catch(() => setInfo(null));
  }, [a.online]);
  if (!info) return <p class="muted">{t.needsOnline}</p>;
  const save = async () => {
    setMsg(null);
    try {
      await app.api('/api/admin/facility', {
        method: 'PUT',
        body: JSON.stringify({ dhis2OrgUnit: ou.trim() || null, mfrId: mfr.trim() || null }),
      });
      setMsg(t.saved);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    }
  };
  return (
    <div class="card" data-card="facility-ids">
      <h3>{t.facilityIds}</h3>
      <p class="muted">
        {info.dhis2.configured ? t.dhis2Configured(!!info.dhis2.dryRun) : t.dhis2NotConfigured}
      </p>
      <div class="field">
        <label for="ou">{t.dhis2OrgUnit}</label>
        <input
          id="ou"
          value={ou}
          autocomplete="off"
          onInput={(e) => setOu(e.currentTarget.value)}
        />
      </div>
      <div class="field">
        <label for="mfr">{t.mfrId}</label>
        <input
          id="mfr"
          value={mfr}
          autocomplete="off"
          onInput={(e) => setMfr(e.currentTarget.value)}
        />
      </div>
      <div class="actions left">
        <button type="button" class="primary" onClick={() => void save()}>
          {t.save}
        </button>
        {msg ? <span class="muted">{msg}</span> : null}
      </div>
    </div>
  );
}

/** Records removed as entered in error, newest first, each with who, when, why and Restore. */
function RemovedRecords() {
  const a = useApp();
  const [items, setItems] = useState<Array<{
    key: string;
    title: string;
    href: string;
    record: Patient | EpisodeOfCare;
    at: string;
  }> | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: reload when the store changes
  useEffect(() => {
    const s = app.session;
    if (!s) return;
    void Promise.all([s.list<Patient>('Patient'), s.list<EpisodeOfCare>('EpisodeOfCare')]).then(
      ([patients, episodes]) => {
        const byId = new Map(patients.map((p) => [p.id, p]));
        const label = (p: Patient | undefined) =>
          p ? `${displayName(p)}${mrnOf(p) ? ` · MRN ${mrnOf(p)}` : ''}` : '—';
        const out = [
          ...patients
            .filter(isRemovedPatient)
            .map((p) => ({ key: p.id, title: label(p), href: `#/patient/${p.id}`, record: p })),
          ...episodes
            .filter((e) => isRemovedEpisode(e) && removalOf(e))
            .map((e) => {
              const pid = e.patient.reference.slice('Patient/'.length);
              return {
                key: e.id,
                title: t.removedEpisodeOf(label(byId.get(pid))),
                href: `#/patient/${pid}/record`,
                record: e,
              };
            }),
        ].map((x) => ({ ...x, at: removalOf(x.record)?.at ?? '' }));
        setItems(out.sort((x, y) => y.at.localeCompare(x.at)));
      },
    );
  }, [a.status]);
  return (
    <div class="card" data-card="removed">
      <h3>{t.removedListTitle}</h3>
      <p class="muted">{t.removedListHelp}</p>
      {items && !items.length ? <p class="empty">{t.removedNone}</p> : null}
      {(items ?? []).map((x) => (
        <div key={x.key} class="removed-item">
          <a href={x.href}>{x.title}</a>
          <RemovedBanner record={x.record} />
        </div>
      ))}
    </div>
  );
}
